// Pure helpers for the Notemaker page: colour swatches, the client-side HTML
// allow-list (mirrors hrms routers/notes.py so what the user sees after a
// paste is what the server will keep), HTML → text/Markdown, and small
// formatting helpers. No React, no network — everything here is unit-testable.
import type { NoteColor } from "../../api/notes";

// ── Colours ───────────────────────────────────────────────────────────
// Soft tints on the cream/forest theme. `tint` is the card/editor wash,
// `stripe` the saturated accent (swatch dot, card edge), `ink` a readable
// text colour on the tint for the list's colour label.
export interface NoteSwatch {
  key: NoteColor;
  label: string;
  tint: string;
  stripe: string;
  ink: string;
}

export const NOTE_SWATCHES: NoteSwatch[] = [
  { key: "default", label: "Paper", tint: "#ffffff", stripe: "#d6d0c2", ink: "#4a4a4a" },
  { key: "sand", label: "Sand", tint: "#f8efdd", stripe: "#d9b86a", ink: "#6b4f10" },
  { key: "mint", label: "Mint", tint: "#e6f3ea", stripe: "#5ea77a", ink: "#1f5a37" },
  { key: "sky", label: "Sky", tint: "#e4eff9", stripe: "#5c92cf", ink: "#1d4b7c" },
  { key: "lilac", label: "Lilac", tint: "#ede7f7", stripe: "#8d74c9", ink: "#43307a" },
  { key: "rose", label: "Rose", tint: "#fbe7ea", stripe: "#d9707f", ink: "#7d2a38" },
  { key: "lemon", label: "Lemon", tint: "#fbf6d9", stripe: "#d4bd3a", ink: "#665a0b" },
  { key: "peach", label: "Peach", tint: "#fdeadf", stripe: "#e5915f", ink: "#7a3d17" },
];

export const swatchFor = (color: string | null | undefined): NoteSwatch =>
  NOTE_SWATCHES.find((s) => s.key === color) || NOTE_SWATCHES[0];

// ── Text / cell colours ───────────────────────────────────────────────
// A fixed palette, NOT free hex: the markup carries a token name
// (`<span data-color="red">`, `<td data-fill="red">`) and notemaker.css maps
// it to a var per theme, so a coloured cell stays readable in the
// messenger's dark/high-contrast shells. A pasted inline `style` is still
// dropped by the sanitizer — a baked-in hex would be invisible in dark mode.
export const NOTE_INK_KEYS = ["red", "orange", "amber", "green", "teal", "blue", "violet", "grey"] as const;
export type NoteInkKey = (typeof NOTE_INK_KEYS)[number];

export const INK_LABELS: Record<NoteInkKey, string> = {
  red: "Red", orange: "Orange", amber: "Amber", green: "Green",
  teal: "Teal", blue: "Blue", violet: "Violet", grey: "Grey",
};

export const isInkKey = (value: string): value is NoteInkKey =>
  (NOTE_INK_KEYS as readonly string[]).includes(value);

// ── Column widths ─────────────────────────────────────────────────────
// A resized column carries a BUCKET, not a width: `data-w="5"` on the cell
// means 5 × 5% = 25% of the table's width, and notemaker.css maps the 20
// buckets (1…20 → 5%…100%) to a `width:` rule. It has to be an attribute
// — every sanitizer drops inline `style` — and it has to be proportional:
// `.nm-content table` is `table-layout: fixed; width: 100%`, so a note saved
// with pixel widths would not reflow in the narrower messenger and phone
// views.
export const NOTE_COL_W_MAX = 20;
/** Strict: only "1"…"20", so a pasted "0", "25", "01" or "1.5" is dropped. */
export const isColWidth = (value: string): boolean =>
  /^(?:[1-9]|1\d|20)$/.test(value);

// ── HTML allow-list (keep in sync with hrms routers/notes.py) ─────────
const ALLOWED_TAGS = new Set([
  "p", "br", "div", "span", "b", "strong", "i", "em", "u", "s", "strike", "del",
  "h1", "h2", "h3", "ul", "ol", "li", "blockquote", "pre", "code", "a", "hr",
  "mark", "sub", "sup",
  // The toolbar's Table tool; no colspan/rowspan (the row/column tools
  // assume a plain grid).
  "table", "thead", "tbody", "tr", "th", "td",
]);
// The grid tags. A table nested in a td/th is unwrapped (its text stays): the
// row/column, formula, sort and filter tools all read cellIndex / rowIndex and
// assume a plain grid — the same reason colspan/rowspan are not allowed.
// Keep in sync with notes.py::_TABLE_TAGS and the phone's TABLE_TAGS.
const TABLE_TAGS = new Set(["table", "thead", "tbody", "tr", "th", "td"]);
const DROP_WITH_CONTENT = new Set([
  "script", "style", "iframe", "object", "embed", "template", "noscript",
  "svg", "math", "head", "title", "meta", "link", "form", "input", "button",
  "textarea", "select", "img", "video", "audio", "canvas",
]);
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(["href", "title"]),
  ul: new Set(["data-checklist"]),
  li: new Set(["data-checked"]),
  // A row the column filter hides; the row keeps every cell (see sortRowOrder).
  tr: new Set(["data-filtered"]),
  // Palette token for text colour (never a raw style — see NOTE_INK_KEYS).
  span: new Set(["data-color"]),
  // Excel-style cell formula; the cell text holds its computed value.
  // data-fill is the cell's background token, data-w the column-width bucket.
  td: new Set(["data-formula", "data-fill", "data-color", "data-w"]),
  // data-sort marks the column the rows are sorted by — header cells only.
  th: new Set(["data-formula", "data-fill", "data-color", "data-w", "data-sort"]),
};
const SAFE_HREF = /^(https?:\/\/|mailto:|tel:)/i;

const parseFragment = (html: string): HTMLElement => {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  return doc.body;
};

function cleanNode(node: Node, out: Node, doc: Document): void {
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      out.appendChild(doc.createTextNode(child.textContent || ""));
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const el = child as HTMLElement;
    const tag = el.tagName.toLowerCase();
    if (DROP_WITH_CONTENT.has(tag)) return;
    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap: the tag goes, its children stay.
      cleanNode(el, out, doc);
      return;
    }
    if (TABLE_TAGS.has(tag) && (out as HTMLElement).closest?.("td,th")) {
      // Never a table inside a table cell (see TABLE_TAGS): unwrap it, so the
      // pasted cells' text survives — a space between cells and a line per
      // row, the spacing htmlToText gives a table.
      if (tag === "td" || tag === "th") out.appendChild(doc.createTextNode(" "));
      cleanNode(el, out, doc);
      if (tag === "tr") out.appendChild(doc.createElement("br"));
      return;
    }
    const clean = doc.createElement(tag);
    const allowed = ALLOWED_ATTRS[tag];
    if (allowed) {
      Array.from(el.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (!allowed.has(name)) return;
        let value = attr.value;
        if (tag === "a" && name === "href" && !SAFE_HREF.test(value.trim())) return;
        if (tag === "li" && name === "data-checked") value = value.trim().toLowerCase() === "true" ? "true" : "false";
        if (tag === "ul" && name === "data-checklist") value = "true";
        if (name === "data-filtered") {
          if (value.trim().toLowerCase() !== "true") return;
          value = "true";
        }
        if (name === "data-sort") {
          value = value.trim().toLowerCase();
          if (value !== "asc" && value !== "desc") return;
        }
        if (name === "data-formula") {
          value = value.trim().slice(0, 500);
          if (!value.startsWith("=")) return;
        }
        if (name === "data-color" || name === "data-fill") {
          value = value.trim().toLowerCase();
          if (!isInkKey(value)) return;
        }
        if (name === "data-w") {
          value = value.trim();
          if (!isColWidth(value)) return;
        }
        clean.setAttribute(name, value);
      });
      if (tag === "a" && clean.hasAttribute("href")) {
        clean.setAttribute("target", "_blank");
        clean.setAttribute("rel", "noopener noreferrer");
      }
    }
    if (tag !== "br" && tag !== "hr") cleanNode(el, clean, doc);
    out.appendChild(clean);
  });
}

/** Allow-list sanitizer for editor HTML. Idempotent; safe for innerHTML. */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return "";
  const body = parseFragment(html);
  const doc = body.ownerDocument;
  const out = doc.createElement("div");
  cleanNode(body, out, doc);
  return out.innerHTML.trim();
}

// ── HTML → text / Markdown ────────────────────────────────────────────
const BLOCK_TAGS = new Set(["p", "div", "li", "h1", "h2", "h3", "blockquote", "pre", "ul", "ol", "hr", "tr", "table"]);

/** Plain text with one line per block, blank lines collapsed. */
export function htmlToText(html: string | null | undefined): string {
  if (!html) return "";
  const body = parseFragment(html);
  const parts: string[] = [];
  const walk = (node: Node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        parts.push(child.textContent || "");
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;
      const tag = (child as HTMLElement).tagName.toLowerCase();
      if (DROP_WITH_CONTENT.has(tag)) return;
      if (tag === "br" || tag === "hr") {
        parts.push("\n");
        return;
      }
      // Cells stay on one line per row, separated by a space (mirrors the
      // server's html_to_text so snippets match).
      if (tag === "td" || tag === "th") parts.push(" ");
      walk(child);
      if (BLOCK_TAGS.has(tag)) parts.push("\n");
    });
  };
  walk(body);
  const lines = parts.join("").replace(/ /g, " ").split(/\r?\n/).map((l) => l.replace(/[ \t]+/g, " ").trim());
  const out: string[] = [];
  lines.forEach((line) => {
    if (line || (out.length && out[out.length - 1])) out.push(line);
  });
  return out.join("\n").trim();
}

/** Lightweight Markdown export covering everything the toolbar can produce. */
export function htmlToMarkdown(html: string | null | undefined): string {
  if (!html) return "";
  const body = parseFragment(html);

  const inline = (node: Node): string => {
    let s = "";
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        s += (child.textContent || "").replace(/ /g, " ");
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;
      const el = child as HTMLElement;
      const tag = el.tagName.toLowerCase();
      const inner = () => inline(el);
      switch (tag) {
        case "b": case "strong": s += `**${inner()}**`; break;
        case "i": case "em": s += `*${inner()}*`; break;
        case "u": s += `_${inner()}_`; break;
        case "s": case "strike": case "del": s += `~~${inner()}~~`; break;
        case "code": s += `\`${inner()}\``; break;
        case "mark": s += `==${inner()}==`; break;
        case "br": s += "  \n"; break;
        case "a": {
          const href = el.getAttribute("href");
          s += href ? `[${inner()}](${href})` : inner();
          break;
        }
        default: s += block(el, 0);
      }
    });
    return s;
  };

  const block = (node: Node, depth: number): string => {
    let s = "";
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const t = (child.textContent || "").replace(/ /g, " ");
        if (t.trim()) s += t;
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;
      const el = child as HTMLElement;
      const tag = el.tagName.toLowerCase();
      const pad = "  ".repeat(depth);
      switch (tag) {
        case "h1": s += `# ${inline(el).trim()}\n\n`; break;
        case "h2": s += `## ${inline(el).trim()}\n\n`; break;
        case "h3": s += `### ${inline(el).trim()}\n\n`; break;
        case "p": case "div": {
          const text = inline(el).trim();
          s += text ? `${text}\n\n` : "";
          break;
        }
        case "blockquote":
          s += block(el, depth).trim().split("\n").map((l) => `> ${l}`).join("\n") + "\n\n";
          break;
        case "pre": s += "```\n" + (el.textContent || "").replace(/\n$/, "") + "\n```\n\n"; break;
        case "hr": s += "---\n\n"; break;
        case "table": {
          // GitHub-style pipe table; the first row is the header.
          const rows = Array.from(el.querySelectorAll("tr")).map((tr) =>
            Array.from(tr.children).map((cell) => inline(cell).trim().replace(/\|/g, "\\|"))
          );
          if (!rows.length) break;
          const width = Math.max(...rows.map((r) => r.length));
          const line = (cells: string[]) => `| ${Array.from({ length: width }, (_, i) => cells[i] || "").join(" | ")} |`;
          s += `${line(rows[0])}\n| ${Array(width).fill("---").join(" | ")} |\n`;
          rows.slice(1).forEach((r) => { s += `${line(r)}\n`; });
          s += "\n";
          break;
        }
        case "ul": case "ol": {
          const checklist = tag === "ul" && el.getAttribute("data-checklist") === "true";
          let i = 0;
          Array.from(el.children).forEach((li) => {
            if (li.tagName.toLowerCase() !== "li") return;
            i += 1;
            const marker = checklist
              ? (li.getAttribute("data-checked") === "true" ? "- [x] " : "- [ ] ")
              : tag === "ol" ? `${i}. ` : "- ";
            // Nested lists render after the item's own inline text.
            const own = document.createElement("li");
            const nested: HTMLElement[] = [];
            Array.from(li.childNodes).forEach((n) => {
              const t = n.nodeType === Node.ELEMENT_NODE ? (n as HTMLElement).tagName.toLowerCase() : "";
              if (t === "ul" || t === "ol") nested.push(n as HTMLElement);
              else own.appendChild(n.cloneNode(true));
            });
            s += `${pad}${marker}${inline(own).trim()}\n`;
            nested.forEach((n) => { s += block(wrap(n), depth + 1); });
          });
          s += depth === 0 ? "\n" : "";
          break;
        }
        default: s += inline(el);
      }
    });
    return s;
  };

  const wrap = (el: HTMLElement): HTMLElement => {
    const d = document.createElement("div");
    d.appendChild(el.cloneNode(true));
    return d;
  };

  return block(body, 0).replace(/\n{3,}/g, "\n\n").trim();
}

// ── Small formatting helpers ──────────────────────────────────────────
export const countWords = (text: string): number => (text.match(/\S+/g) || []).length;

/** "hr", "Team Sync" → "team-sync"; mirrors the server's tag normaliser. */
export const normalizeTag = (raw: string): string =>
  raw.trim().replace(/^#+/, "").trim().toLowerCase().replace(/\s+/g, "-").slice(0, 30);

/** "just now", "4 min ago", "Yesterday", "12 Aug", "12 Aug 2025". */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const diff = Math.max(0, now.getTime() - then.getTime());
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24 && then.getDate() === now.getDate()) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (then.toDateString() === yesterday.toDateString()) return "Yesterday";
  const sameYear = then.getFullYear() === now.getFullYear();
  return then.toLocaleDateString("en-IN", sameYear ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" });
}

export function fullTimestamp(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** Safe file name for exports: "Sprint notes!" → "Sprint-notes". */
export const fileSafeName = (title: string): string =>
  (title || "Untitled note").replace(/[^a-z0-9\- ]/gi, "").trim().replace(/\s+/g, "-").slice(0, 60) || "note";

export function downloadTextFile(name: string, content: string, mime = "text/plain"): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The editor treats a body with no visible text and no structure as empty. */
export const isBlankHtml = (html: string): boolean => htmlToText(html).length === 0 && !/<(ul|ol|hr|table)/i.test(html);

// ── Table formulas (Excel-style) ──────────────────────────────────────
// A formula cell carries data-formula="=SUM(A2:A4)" and its text is the
// computed value, so search, Markdown, mobile and read-only views all see
// values. Addresses are Excel's: columns A, B, …, rows 1-based counting the
// header row. A row or column that is inserted, deleted, moved or sorted
// takes the refs that point at it with it — see rewriteFormulaRefs below for
// how, and for what "$" means.

export const FORMULA_MAX = 500;

export const FORMULA_FUNCTIONS = [
  "SUM", "AVERAGE", "MIN", "MAX", "COUNT", "COUNTA", "COUNTBLANK", "PRODUCT", "MEDIAN",
  "ROUND", "ROUNDUP", "ROUNDDOWN", "INT", "ABS", "SQRT", "POWER", "MOD",
  "IF", "IFERROR", "AND", "OR", "NOT", "SUMIF", "COUNTIF", "AVERAGEIF",
  "CONCAT", "LEN", "UPPER", "LOWER", "TRIM", "LEFT", "RIGHT", "TODAY",
];

/** 0 → "A", 25 → "Z", 26 → "AA". */
export const colName = (i: number): string => {
  let s = "";
  let n = i + 1;
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

/** What a ref becomes when the row or column it pointed at is gone. */
export const REF_DEAD = "#REF!";

const colIndex = (letters: string): number =>
  letters.toUpperCase().split("").reduce((n, ch) => n * 26 + (ch.charCodeAt(0) - 64), 0) - 1;

type Scalar = number | string | boolean | null;
type Val = Scalar | Scalar[];

class FormulaError extends Error {
  constructor(public code: string) { super(code); }
}
const fail = (code: string): never => { throw new FormulaError(code); };

/**
 * One address exactly as the formula spells it: which row and column it
 * means, and whether each half was written with a "$". The tokenizer keeps
 * these beside the indices it hands the parser so that rewriteFormulaRefs
 * can put a moved address back in the writer's own spelling; evaluation
 * ignores them (see the "$" note in the rewriting block below).
 */
interface RefPart { r: number; c: number; absR: boolean; absC: boolean; }

/** Where a ref or range token sits in the source, and how it was written. */
interface RefSrc { at: number; len: number; parts: RefPart[] }

type Tok =
  | { t: "num"; v: number }
  | { t: "str"; v: string }
  | { t: "ref"; r: number; c: number; src: RefSrc }
  | { t: "range"; r1: number; c1: number; r2: number; c2: number; src: RefSrc }
  | { t: "err"; v: string }
  | { t: "id"; v: string }
  | { t: "op"; v: string };

const REF_RE = /^(\$?)([A-Za-z]{1,3})(\$?)(\d+)/;

/** A matched address as written: $-flags kept, indices 0-based. */
const refPart = (m: RegExpExecArray): RefPart =>
  ({ r: parseInt(m[4], 10) - 1, c: colIndex(m[2]), absR: !!m[3], absC: !!m[1] });

function tokenize(src: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (/\s/.test(ch)) { i += 1; continue; }
    const rest = src.slice(i);
    const num = /^\d+(\.\d+)?(%?)|^\.\d+(%?)/.exec(rest);
    if (num) {
      let v = parseFloat(num[0]);
      if (num[0].endsWith("%")) v /= 100;
      out.push({ t: "num", v });
      i += num[0].length;
      continue;
    }
    if (ch === '"') {
      let j = i + 1;
      let s = "";
      while (j < src.length) {
        if (src[j] === '"') {
          if (src[j + 1] === '"') { s += '"'; j += 2; continue; }
          break;
        }
        s += src[j];
        j += 1;
      }
      if (j >= src.length) fail("#VALUE!");
      out.push({ t: "str", v: s });
      i = j + 1;
      continue;
    }
    const ref = REF_RE.exec(rest);
    // A ref must not run into more letters/digits/"(" (that is a function name).
    if (ref && !/^[A-Za-z0-9_(]/.test(rest.slice(ref[0].length))) {
      const a = refPart(ref);
      const after = rest.slice(ref[0].length);
      const second = after.startsWith(":") ? REF_RE.exec(after.slice(1)) : null;
      if (second) {
        const b = refPart(second);
        const len = ref[0].length + 1 + second[0].length;
        out.push({
          t: "range",
          r1: Math.min(a.r, b.r), c1: Math.min(a.c, b.c),
          r2: Math.max(a.r, b.r), c2: Math.max(a.c, b.c),
          src: { at: i, len, parts: [a, b] },
        });
        i += len;
      } else {
        out.push({ t: "ref", r: a.r, c: a.c, src: { at: i, len: ref[0].length, parts: [a] } });
        i += ref[0].length;
      }
      continue;
    }
    // A dead address the rewriter wrote (see rewriteFormulaRefs): the formula
    // still parses, and evaluating it gives #REF! — Excel's own behaviour.
    if (rest.startsWith(REF_DEAD)) {
      out.push({ t: "err", v: REF_DEAD });
      i += REF_DEAD.length;
      continue;
    }
    const id = /^[A-Za-z_][A-Za-z0-9_.]*/.exec(rest);
    if (id) {
      out.push({ t: "id", v: id[0].toUpperCase() });
      i += id[0].length;
      continue;
    }
    const op = /^(<=|>=|<>|[-+*/^&=<>(),;:])/.exec(rest);
    if (op) {
      out.push({ t: "op", v: op[0] === ";" ? "," : op[0] });
      i += op[0].length;
      continue;
    }
    fail("#NAME?");
  }
  return out;
}

type FNode =
  | { k: "lit"; v: Scalar }
  | { k: "err"; v: string }
  | { k: "ref"; r: number; c: number }
  | { k: "range"; r1: number; c1: number; r2: number; c2: number }
  | { k: "neg"; a: FNode }
  | { k: "bin"; op: string; a: FNode; b: FNode }
  | { k: "call"; name: string; args: FNode[] };

function parse(tokens: Tok[]): FNode {
  let p = 0;
  const peek = () => tokens[p];
  const isOp = (v: string) => peek()?.t === "op" && (peek() as { v: string }).v === v;
  const eat = (v: string) => { if (!isOp(v)) fail("#ERROR!"); p += 1; };

  const binary = (ops: string[], next: () => FNode) => (): FNode => {
    let left = next();
    while (peek()?.t === "op" && ops.includes((peek() as { v: string }).v)) {
      const op = (tokens[p] as { v: string }).v;
      p += 1;
      left = { k: "bin", op, a: left, b: next() };
    }
    return left;
  };

  const primary = (): FNode => {
    const tok = tokens[p];
    if (!tok) return fail("#ERROR!");
    p += 1;
    switch (tok.t) {
      case "num": return { k: "lit", v: tok.v };
      case "str": return { k: "lit", v: tok.v };
      case "err": return { k: "err", v: tok.v };
      case "ref": return { k: "ref", r: tok.r, c: tok.c };
      case "range": return { k: "range", r1: tok.r1, c1: tok.c1, r2: tok.r2, c2: tok.c2 };
      case "id": {
        if (isOp("(")) {
          p += 1;
          const args: FNode[] = [];
          if (!isOp(")")) {
            args.push(comparison());
            while (isOp(",")) { p += 1; args.push(comparison()); }
          }
          eat(")");
          return { k: "call", name: tok.v, args };
        }
        if (tok.v === "TRUE" || tok.v === "FALSE") return { k: "lit", v: tok.v === "TRUE" };
        return fail("#NAME?");
      }
      case "op":
        if (tok.v === "(") { const inner = comparison(); eat(")"); return inner; }
        if (tok.v === "-") return { k: "neg", a: unary() };
        if (tok.v === "+") return unary();
        return fail("#ERROR!");
    }
    return fail("#ERROR!");
  };
  const unary = (): FNode => primary();
  const power = binary(["^"], unary);
  const mul = binary(["*", "/"], power);
  const add = binary(["+", "-"], mul);
  const concat = binary(["&"], add);
  const comparison = binary(["=", "<>", "<", ">", "<=", ">="], concat);

  const tree = comparison();
  if (p !== tokens.length) fail("#ERROR!");
  return tree;
}

const toNum = (v: Scalar): number => {
  if (v === null || v === "") return 0;
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  const n = parseCellNumber(v);
  return n === null ? fail("#VALUE!") : n;
};
const toStr = (v: Scalar): string =>
  v === null ? "" : typeof v === "boolean" ? (v ? "TRUE" : "FALSE") : typeof v === "number" ? formatNum(v) : v;
const toBool = (v: Scalar): boolean => (typeof v === "string" ? (v.toUpperCase() === "TRUE" ? true : v.toUpperCase() === "FALSE" ? false : fail("#VALUE!")) : toNum(v) !== 0);

/** "1,200" → 1200, "₹ 50" → 50, "12%" → 0.12; anything else → null. */
export function parseCellNumber(raw: string): number | null {
  const s = raw.trim().replace(/^[₹$€£]\s*/, "").replace(/,/g, "");
  if (!/^[-+]?(\d+(\.\d*)?|\.\d+)(e[-+]?\d+)?%?$/i.test(s)) return null;
  const n = parseFloat(s);
  return s.endsWith("%") ? n / 100 : n;
}

const formatNum = (n: number): string => {
  if (!Number.isFinite(n)) return fail("#NUM!");
  // Trim float noise (0.1 + 0.2) without losing real precision.
  return String(Number.parseFloat(n.toPrecision(12)));
};

const flat = (v: Val): Scalar[] => (Array.isArray(v) ? v : [v]);
/** Numbers from ranges ignore text/blanks (Excel); direct args are coerced. */
const numbersOf = (vals: Val[]): number[] =>
  vals.flatMap((v) => (Array.isArray(v)
    ? v.filter((x): x is number | string => typeof x === "number" || (typeof x === "string" && parseCellNumber(x) !== null)).map((x) => toNum(x))
    : [toNum(v)]));

function criteria(crit: Scalar): (v: Scalar) => boolean {
  if (typeof crit === "number" || typeof crit === "boolean") return (v) => v !== null && v !== "" && toNum(v) === toNum(crit);
  const m = /^(<=|>=|<>|<|>|=)?(.*)$/.exec(String(crit ?? ""))!;
  const op = m[1] || "=";
  const target = m[2];
  const tNum = parseCellNumber(target);
  return (v) => {
    const vNum = typeof v === "number" ? v : typeof v === "string" ? parseCellNumber(v) : null;
    if (tNum !== null && vNum !== null) {
      switch (op) {
        case "=": return vNum === tNum;
        case "<>": return vNum !== tNum;
        case "<": return vNum < tNum;
        case ">": return vNum > tNum;
        case "<=": return vNum <= tNum;
        default: return vNum >= tNum;
      }
    }
    const a = toStr(v).toLowerCase();
    const b = target.toLowerCase();
    if (op === "=") return a === b;
    if (op === "<>") return a !== b;
    return false;
  };
}

const round = (n: number, digits: number, mode: "near" | "up" | "down"): number => {
  const f = 10 ** digits;
  const x = Math.abs(n) * f;
  const r = mode === "near" ? Math.round(x + 1e-9) : mode === "up" ? Math.ceil(x - 1e-9) : Math.floor(x + 1e-9);
  return (Math.sign(n) * r) / f;
};

export interface FormulaCell {
  /** The cell's data-formula ("=…"), or null for a plain value cell. */
  formula: string | null;
  /** The cell's visible text (used for plain value cells). */
  text: string;
}

/**
 * Evaluate every formula cell of one table. Returns a grid of display
 * strings for formula cells (an Excel-style "#…!" code on error) and null
 * for plain cells, which the caller leaves untouched.
 */
export function evaluateTable(grid: FormulaCell[][]): (string | null)[][] {
  const memo = new Map<string, Scalar | FormulaError>();
  const visiting = new Set<string>();

  const cellValue = (r: number, c: number): Scalar => {
    const cell = grid[r]?.[c];
    if (!cell) return fail("#REF!");
    if (!cell.formula) {
      const text = cell.text.replace(/ /g, " ").trim();
      if (!text) return null;
      const n = parseCellNumber(text);
      return n === null ? text : n;
    }
    const key = `${r}:${c}`;
    const hit = memo.get(key);
    if (hit instanceof FormulaError) throw hit;
    if (hit !== undefined) return hit;
    if (visiting.has(key)) return fail("#CIRC!");
    visiting.add(key);
    try {
      const v = evalFormula(cell.formula);
      memo.set(key, v);
      return v;
    } catch (e) {
      const err = e instanceof FormulaError ? e : new FormulaError("#ERROR!");
      memo.set(key, err);
      throw err;
    } finally {
      visiting.delete(key);
    }
  };

  const evalNode = (n: FNode): Val => {
    switch (n.k) {
      case "lit": return n.v;
      case "err": return fail(n.v);
      case "ref": return cellValue(n.r, n.c);
      case "range": {
        if (n.r2 >= grid.length || n.c2 >= Math.max(...grid.map((row) => row.length))) fail("#REF!");
        const vals: Scalar[] = [];
        for (let r = n.r1; r <= n.r2; r += 1) for (let c = n.c1; c <= n.c2; c += 1) vals.push(grid[r]?.[c] ? cellValue(r, c) : null);
        return vals;
      }
      case "neg": return -toNum(scalar(n.a));
      case "bin": return binaryOp(n.op, scalar(n.a), scalar(n.b));
      case "call": return call(n.name, n.args);
    }
    return fail("#ERROR!");
  };
  const scalar = (n: FNode): Scalar => {
    const v = evalNode(n);
    if (Array.isArray(v)) return v.length === 1 ? v[0] : fail("#VALUE!");
    return v;
  };

  const binaryOp = (op: string, a: Scalar, b: Scalar): Scalar => {
    switch (op) {
      case "+": return toNum(a) + toNum(b);
      case "-": return toNum(a) - toNum(b);
      case "*": return toNum(a) * toNum(b);
      case "/": return toNum(b) === 0 ? fail("#DIV/0!") : toNum(a) / toNum(b);
      case "^": return toNum(a) ** toNum(b);
      case "&": return toStr(a) + toStr(b);
      default: {
        const numeric = (typeof a === "number" || a === null) && (typeof b === "number" || b === null);
        const x: number | string = numeric ? toNum(a) : toStr(a).toLowerCase();
        const y: number | string = numeric ? toNum(b) : toStr(b).toLowerCase();
        switch (op) {
          case "=": return x === y;
          case "<>": return x !== y;
          case "<": return x < y;
          case ">": return x > y;
          case "<=": return x <= y;
          default: return x >= y;
        }
      }
    }
  };

  const call = (name: string, argNodes: FNode[]): Val => {
    const need = (min: number, max = min) => {
      if (argNodes.length < min || argNodes.length > max) fail("#N/A");
    };
    // Lazy ones first: only the taken branch is evaluated.
    if (name === "IF") {
      need(2, 3);
      return toBool(scalar(argNodes[0])) ? scalar(argNodes[1]) : argNodes[2] ? scalar(argNodes[2]) : false;
    }
    if (name === "IFERROR") {
      need(2);
      try { return scalar(argNodes[0]); } catch (e) { if (e instanceof FormulaError) return scalar(argNodes[1]); throw e; }
    }
    const args = argNodes.map(evalNode);
    const one = (i = 0) => (Array.isArray(args[i]) ? fail("#VALUE!") : (args[i] as Scalar));
    switch (name) {
      case "SUM": return numbersOf(args).reduce((s, x) => s + x, 0);
      case "PRODUCT": return numbersOf(args).reduce((s, x) => s * x, 1);
      case "AVERAGE": case "AVG": {
        const ns = numbersOf(args);
        return ns.length ? ns.reduce((s, x) => s + x, 0) / ns.length : fail("#DIV/0!");
      }
      case "MIN": { const ns = numbersOf(args); return ns.length ? Math.min(...ns) : 0; }
      case "MAX": { const ns = numbersOf(args); return ns.length ? Math.max(...ns) : 0; }
      case "MEDIAN": {
        const ns = numbersOf(args).sort((a, b) => a - b);
        if (!ns.length) return fail("#NUM!");
        const mid = Math.floor(ns.length / 2);
        return ns.length % 2 ? ns[mid] : (ns[mid - 1] + ns[mid]) / 2;
      }
      case "COUNT": return args.flatMap(flat).filter((v) => typeof v === "number").length;
      case "COUNTA": return args.flatMap(flat).filter((v) => v !== null && v !== "").length;
      case "COUNTBLANK": return args.flatMap(flat).filter((v) => v === null || v === "").length;
      case "ROUND": case "ROUNDUP": case "ROUNDDOWN":
        need(1, 2);
        return round(toNum(one(0)), argNodes[1] ? Math.trunc(toNum(one(1))) : 0, name === "ROUND" ? "near" : name === "ROUNDUP" ? "up" : "down");
      case "INT": need(1); return Math.floor(toNum(one()));
      case "ABS": need(1); return Math.abs(toNum(one()));
      case "SQRT": { need(1); const x = toNum(one()); return x < 0 ? fail("#NUM!") : Math.sqrt(x); }
      case "POWER": need(2); return toNum(one(0)) ** toNum(one(1));
      case "MOD": {
        need(2);
        const d = toNum(one(1));
        if (d === 0) return fail("#DIV/0!");
        const x = toNum(one(0));
        return x - d * Math.floor(x / d);
      }
      case "AND": return args.flatMap(flat).filter((v) => v !== null).every((v) => toBool(v));
      case "OR": return args.flatMap(flat).filter((v) => v !== null).some((v) => toBool(v));
      case "NOT": need(1); return !toBool(one());
      case "SUMIF": case "AVERAGEIF": case "COUNTIF": {
        need(2, name === "COUNTIF" ? 2 : 3);
        const range = flat(args[0]);
        const test = criteria(one(1));
        const sumRange = args[2] !== undefined ? flat(args[2]) : range;
        const hits = range.map((v, i) => (test(v) ? i : -1)).filter((i) => i >= 0);
        if (name === "COUNTIF") return hits.length;
        const ns = hits.map((i) => sumRange[i]).filter((v) => typeof v === "number") as number[];
        if (name === "SUMIF") return ns.reduce((s, x) => s + x, 0);
        return ns.length ? ns.reduce((s, x) => s + x, 0) / ns.length : fail("#DIV/0!");
      }
      case "CONCAT": case "CONCATENATE": return args.flatMap(flat).map(toStr).join("");
      case "LEN": need(1); return toStr(one()).length;
      case "UPPER": need(1); return toStr(one()).toUpperCase();
      case "LOWER": need(1); return toStr(one()).toLowerCase();
      case "TRIM": need(1); return toStr(one()).trim().replace(/\s+/g, " ");
      case "LEFT": case "RIGHT": {
        need(1, 2);
        const s = toStr(one(0));
        const count = argNodes[1] ? Math.max(0, Math.trunc(toNum(one(1)))) : 1;
        return name === "LEFT" ? s.slice(0, count) : s.slice(Math.max(0, s.length - count));
      }
      case "TODAY": {
        need(0);
        const d = new Date();
        return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
      }
      default: return fail("#NAME?");
    }
  };

  const evalFormula = (formula: string): Scalar => {
    const src = formula.trim().replace(/^=/, "");
    if (!src) return fail("#ERROR!");
    const v = evalNode(parse(tokenize(src)));
    if (Array.isArray(v)) return v.length === 1 ? v[0] : fail("#VALUE!");
    return v;
  };

  return grid.map((row, r) => row.map((cell, c) => {
    if (!cell.formula) return null;
    try {
      const v = cellValue(r, c);
      return v === null ? "0" : toStr(v);
    } catch (e) {
      return e instanceof FormulaError ? e.code : "#ERROR!";
    }
  }));
}

// ── A1 reference rewriting ────────────────────────────────────────────
// What a formula POINTS AT has to follow the cells around: a column that
// moves, a row that is inserted, deleted or sorted into a new place, and a
// formula copied to another cell all change which address means which data.
// `rewriteFormulaRefs` is the one place that knows how, and every table tool
// in the editor goes through it. Nothing else may touch a formula's text.
//
// It works on TOKENS, never on the raw string: `tokenize` has already decided
// what is an address, so a "B2" inside a quoted string, a function name that
// starts like one (B2X, LEFT) and a number are all left exactly as they are.
// Only the tokens that actually move are spliced out of the source, so the
// rest of the formula — spacing, casing, the writer's own spelling — survives
// untouched. A formula the tokenizer cannot read is returned unchanged.
//
// WHAT "$" MEANS, as of this change. Before it, "$" was parsed and thrown
// away, so $A$1 and A1 were the same thing. Now it marks an address as
// LOCKED AGAINST TRANSLATION:
//
//   • A copy/paste translates by a (row, column) delta (`dr` / `dc`) and
//     skips the halves written with a "$" — "=$A2" pasted a column right is
//     still "=$A3", Excel's rule.
//   • A structural change — a row or column inserted, deleted, moved or
//     sorted — goes through an index MAP (`rows` / `cols`) and moves every
//     address, "$" or not. The data itself moved, so the address of that
//     data changed; locking is about where a formula is copied TO, not about
//     pinning a ref while its column is dragged out from under it. Excel
//     does the same.
//   • EVALUATION still ignores "$" — $A$1 and A1 read the same cell — which
//     is also Excel. So "$" is now meaningful without changing any result:
//     it only decides what happens to the TEXT when things move.
//
// A ref whose row or column is gone (a map entry of -1) becomes the literal
// #REF!, which the tokenizer reads back as an error value, so "=SUM(A2:A4)/B1"
// can lose B1 and still add up. An address translated off the top or left
// edge is #REF! too: there is no row above 1 and no column left of A. There
// is deliberately NO far-edge clamp — a table grows, so a ref past the last
// row is left as an address and merely evaluates to #REF! until the table
// reaches it, exactly as a hand-typed "=A9" in a three-row table does today.

/** How one move changes addresses. A map wins over the matching delta. */
export interface RefRewrite {
  /** Rows to add to every RELATIVE row address; a $-locked row stays put. */
  dr?: number;
  /** Columns to add to every RELATIVE column address. */
  dc?: number;
  /** old row index → new row index, -1 for a row that is gone. Moves
   *  $-locked addresses too; an index the map does not cover (a ref that was
   *  already off the grid) is left alone. */
  rows?: ReadonlyArray<number>;
  /** old column index → new column index, -1 for a column that is gone. */
  cols?: ReadonlyArray<number>;
}

/** One axis of a single address, moved; null means the ref is dead. */
const moveIndex = (
  i: number,
  map: ReadonlyArray<number> | undefined,
  delta: number,
  abs: boolean,
): number | null => {
  const next = map ? (i < map.length ? map[i] : i) : abs ? i : i + delta;
  return next < 0 ? null : next;
};

/**
 * One axis of a RANGE's two endpoints, in the order they were written.
 *
 * Through a map a span becomes the SPAN OF WHAT IS LEFT: every index it
 * covered is mapped, and the new endpoints are the lowest and the highest
 * that survive. So deleting a row inside "=SUM(B2:B5)" leaves "=SUM(B2:B4)"
 * and sorting rows leaves a whole-column span whole, even though each
 * individual row inside it went somewhere else. A span with nothing left is
 * dead. (A span covering only PART of a reordered block is the one case this
 * cannot keep honest — the lowest and highest may now straddle rows that
 * were never in it — which is why sorting is offered on a whole table and
 * never on a sub-range.)
 */
const moveSpan = (
  a: RefPart,
  b: RefPart,
  pick: (p: RefPart) => number,
  absOf: (p: RefPart) => boolean,
  map: ReadonlyArray<number> | undefined,
  delta: number,
): [number, number] | null => {
  const ia = pick(a);
  const ib = pick(b);
  if (!map) {
    const na = moveIndex(ia, undefined, delta, absOf(a));
    const nb = moveIndex(ib, undefined, delta, absOf(b));
    return na === null || nb === null ? null : [na, nb];
  }
  const kept: number[] = [];
  for (let i = Math.min(ia, ib); i <= Math.max(ia, ib); i += 1) {
    const to = i < map.length ? map[i] : i;
    if (to >= 0) kept.push(to);
  }
  if (!kept.length) return null;
  const lo = Math.min(...kept);
  const hi = Math.max(...kept);
  // Back in the order the two endpoints were written in.
  return ia <= ib ? [lo, hi] : [hi, lo];
};

/** An address back in the spelling its "$" flags ask for. */
const refText = (p: RefPart, r: number, c: number): string =>
  `${p.absC ? "$" : ""}${colName(c)}${p.absR ? "$" : ""}${r + 1}`;

/**
 * `formula` ("=…") with every address moved by `move`. Pure: it reads and
 * returns strings only. Anything that is not a formula comes back unchanged.
 */
export function rewriteFormulaRefs(formula: string, move: RefRewrite): string {
  const eq = /^\s*=/.exec(formula || "");
  if (!eq) return formula;
  const body = formula.slice(eq[0].length);
  let toks: Tok[];
  try {
    toks = tokenize(body);
  } catch {
    // A formula the engine cannot even read is left exactly as it was.
    return formula;
  }
  const dr = move.dr || 0;
  const dc = move.dc || 0;
  let out = "";
  let taken = 0;
  toks.forEach((tok) => {
    if (tok.t !== "ref" && tok.t !== "range") return;
    const { parts } = tok.src;
    let text: string | null = null;
    if (parts.length === 1) {
      const p = parts[0];
      const r = moveIndex(p.r, move.rows, dr, p.absR);
      const c = moveIndex(p.c, move.cols, dc, p.absC);
      if (r === null || c === null) text = REF_DEAD;
      else if (r !== p.r || c !== p.c) text = refText(p, r, c);
    } else {
      const [p, q] = parts;
      const rows = moveSpan(p, q, (x) => x.r, (x) => x.absR, move.rows, dr);
      const cols = moveSpan(p, q, (x) => x.c, (x) => x.absC, move.cols, dc);
      if (!rows || !cols) text = REF_DEAD;
      else if (rows[0] !== p.r || rows[1] !== q.r || cols[0] !== p.c || cols[1] !== q.c) {
        text = `${refText(p, rows[0], cols[0])}:${refText(q, rows[1], cols[1])}`;
      }
    }
    // Nothing moved: leave the writer's own spelling (case and all) in place.
    if (text === null) return;
    out += body.slice(taken, tok.src.at) + text;
    taken = tok.src.at + tok.src.len;
  });
  if (!taken) return formula;
  return formula.slice(0, eq[0].length) + out + body.slice(taken);
}

/** old → new index map for a row/column INSERTED at `at` in `size` of them. */
export const insertIndexMap = (size: number, at: number): number[] =>
  Array.from({ length: size }, (_, i) => (i < at ? i : i + 1));

/** old → new index map for the row/column at `at` being DELETED. */
export const deleteIndexMap = (size: number, at: number): number[] =>
  Array.from({ length: size }, (_, i) => (i === at ? -1 : i < at ? i : i - 1));

/** old → new index map for the row/column at `from` MOVING to `to`. */
export const moveIndexMap = (size: number, from: number, to: number): number[] => {
  const order = Array.from({ length: size }, (_, i) => i);
  order.splice(to, 0, ...order.splice(from, 1));
  const map = new Array<number>(size).fill(-1);
  order.forEach((was, now) => { map[was] = now; });
  return map;
};

/**
 * old → new index map for rows REORDERED by `order` (new position → old
 * index, as sortRowOrder gives it), with `offset` rows above them that do not
 * move — 1 for a table whose first row is the header.
 */
export const orderIndexMap = (order: ReadonlyArray<number>, offset = 0): number[] => {
  const map = Array.from({ length: offset + order.length }, (_, i) => i);
  order.forEach((was, now) => { map[was + offset] = now + offset; });
  return map;
};

// ── Table row sorting and filtering ───────────────────────────────────
// Both act on the BODY rows of one table (row 1 is the header), and both are
// expressed in the saved HTML, because the note body is the only storage
// there is — no separate data model holds a "view".
//
//  • Sorting PHYSICALLY REORDERS the <tr> elements, so the order survives a
//    save, a reload, Markdown export, the phone and a shared recipient. The
//    sorted column's <th> carries data-sort="asc"|"desc" purely as the
//    indicator (and so the next click can flip it).
//  • Filtering hides rows in place: a row the filter rejects keeps all its
//    cells and gains data-filtered="true", which notemaker.css hides. Nothing
//    is deleted, so clearing the filter is just dropping the attribute, and a
//    renderer that does not hide the attribute (the phone) shows every row
//    rather than losing one.
//
// A table with formulas sorts like any other: the editor rewrites every ref
// through the new row order (rewriteFormulaRefs with `rows` from
// orderIndexMap), so a row-local "=B2*C2" follows its row and a whole-column
// "=SUM(B2:B5)" still covers the same rows. Filtering never moves a row, so
// it needs no rewriting at all — but a formula still sees the hidden rows, as
// there is no SUBTOTAL: a filtered SUM is the whole column's SUM.

export type SortDir = "asc" | "desc";
export type FilterMode = "contains" | "equals";

/** A cell's text as sorting and filtering see it: nbsp → space, collapsed. */
export const cellSortText = (raw: string): string =>
  raw.replace(/ /g, " ").replace(/\s+/g, " ").trim();

/**
 * The order body rows should end up in, as indices into `values` (one entry
 * per body row, each already through cellSortText).
 *
 * The column counts as numeric when every non-blank value parses as a number,
 * so a column of "1,200", "₹ 50" or "12%" sorts by size and not as text.
 * Blanks sort last whichever way the column runs (a spreadsheet's habit), and
 * equal values keep the order they were in, so sorting by one column and then
 * another groups the way you would expect.
 */
export function sortRowOrder(values: string[], dir: SortDir): number[] {
  const nums = values.map((v) => (v ? parseCellNumber(v) : null));
  const numeric = values.every((v, i) => !v || nums[i] !== null);
  const sign = dir === "asc" ? 1 : -1;
  return values
    .map((_, i) => i)
    .sort((a, b) => {
      const blankA = !values[a];
      const blankB = !values[b];
      if (blankA || blankB) return blankA === blankB ? a - b : blankA ? 1 : -1;
      const cmp = numeric
        ? (nums[a] as number) - (nums[b] as number)
        : values[a].localeCompare(values[b], undefined, { numeric: true, sensitivity: "base" });
      return cmp ? sign * cmp : a - b;
    });
}

/** Does one cell pass the filter? Case-insensitive; an empty query passes all. */
export function cellMatchesFilter(value: string, query: string, mode: FilterMode = "contains"): boolean {
  const needle = cellSortText(query).toLowerCase();
  if (!needle) return true;
  const hay = cellSortText(value).toLowerCase();
  return mode === "equals" ? hay === needle : hay.includes(needle);
}

/**
 * The column's distinct non-blank values in first-seen order, for the
 * popover's value picker. Values differing only in case collapse into the
 * first spelling seen, which is also how `equals` compares them.
 */
export function distinctColumnValues(values: string[], limit = 12): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  values.forEach((raw) => {
    const value = cellSortText(raw);
    const key = value.toLowerCase();
    if (!value || seen.has(key)) return;
    seen.add(key);
    if (out.length < limit) out.push(value);
  });
  return out;
}

// ── Cell-range clipboard (tab-separated values) ───────────────────────
// A rectangle of table cells travels on the clipboard in Excel's two
// flavours: text/html as a real table (built in NoteEditor, where the cells
// are) and text/plain as TSV — tab between columns, newline between rows.
// These two are the TSV half, and the pair round-trips: parseTsv(cellsToTsv(g))
// is g, give or take the quoting below.
//
// Quoting is the CSV convention with a tab as the separator: a value holding
// a tab, a newline or a double quote is wrapped in double quotes and its own
// quotes are doubled. Excel, Sheets and LibreOffice all write and read it, so
// a cell with a line break in it survives a trip through any of them.

/** A grid of cell values as Excel's text/plain flavour: TSV. */
export function cellsToTsv(rows: string[][]): string {
  return rows
    .map((row) => row
      .map((raw) => {
        const value = (raw || "").replace(/ /g, " ");
        return /[\t\r\n"]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
      })
      .join("\t"))
    .join("\n");
}

/**
 * TSV back into a grid. The result is always RECTANGULAR — short rows are
 * padded with empty cells — because every grid tool in the editor assumes a
 * plain grid, the same reason colspan/rowspan are not allowed.
 *
 * A single trailing newline is a terminator, not an empty row; a blank line
 * inside the text is a row of one empty cell, which is what Excel puts there.
 */
export function parseTsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  const endRow = () => {
    row.push(cell);
    cell = "";
    rows.push(row);
    row = [];
  };
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      // A doubled quote is one literal quote; a lone one ends the value.
      if (ch !== '"') cell += ch;
      else if (text[i + 1] === '"') { cell += '"'; i += 1; }
      else quoted = false;
      continue;
    }
    // Only a quote that OPENS the value quotes it, so 12" stays 12".
    if (ch === '"' && !cell) { quoted = true; continue; }
    if (ch === "\t") { row.push(cell); cell = ""; continue; }
    if (ch === "\r" || ch === "\n") {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      endRow();
      continue;
    }
    cell += ch;
  }
  if (cell || row.length) endRow();
  const width = rows.reduce((w, r) => Math.max(w, r.length), 0);
  return rows.map((r) => (r.length === width ? r : r.concat(Array(width - r.length).fill(""))));
}
